import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Button, ChevronLeft, LinkExternal01 } from '@opendate-app/setlist';
import UserManualSidebar from '../../UserManual/components/UserManualSidebar';
import UserManualSearch from '../../UserManual/components/UserManualSearch';
import UserManualArticle from '../../UserManual/components/UserManualArticle';
import ConductorSupportRequestsList from './ConductorSupportRequestsList';
import { buildArticleMap, buildArticleList } from '../../UserManual/utils/articleIndex';
import '../../UserManual/styles/userManualStyles.css';
import '../styles/conductorManualPanel.css';

const ConductorManualPanel = ({ sections, loading, error, onRetry, onClose, onAskConductor, requestedArticle = null }) => {
  const [view, setView] = useState('articles');
  const [activeKey, setActiveKey] = useState(null);
  const [lastViewedKey, setLastViewedKey] = useState(null);
  const bodyRef = useRef(null);
  const articleMap = useMemo(() => buildArticleMap(sections || []), [sections]);
  const articleList = useMemo(() => buildArticleList(sections || []), [sections]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeKey]);

  useEffect(() => {
    if (!requestedArticle || !articleMap[requestedArticle.key]) return;
    setView('articles');
    setActiveKey(requestedArticle.key);
    setLastViewedKey(requestedArticle.key);
  }, [requestedArticle, articleMap]);

  const showArticle = (key) => {
    setActiveKey(key);
    setLastViewedKey(key);
  };

  const showingArticles = view === 'articles';
  const activeEntry = activeKey ? articleMap[activeKey] : null;
  const activeIndex = articleList.findIndex((article) => article.key === activeKey);
  const prevArticle = activeIndex > 0 ? articleList[activeIndex - 1] : null;
  const nextArticle = activeIndex >= 0 && activeIndex < articleList.length - 1 ? articleList[activeIndex + 1] : null;
  const fullManualHref = activeKey ? `/user_manual#${activeKey}` : '/user_manual';

  return (
    <aside
      role="dialog"
      aria-label="Help Center"
      className="venue-conductor-manual-panel"
    >
      <header className="venue-conductor-ui-header venue-conductor-manual-panel-header">
        <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 1 }}>
          <Button
            variant="tertiary"
            size="sm"
            icon={ArrowLeft}
            className="venue-conductor-ui-chat-menu"
            aria-label="Back to chat"
            onClick={onClose}
          >
            Back to chat
          </Button>
          <span className="venue-conductor-manual-panel-title">Help Center</span>
        </div>
        <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 2 }}>
          <Button
            as="a"
            variant="tertiary"
            size="sm"
            icon={LinkExternal01}
            href={fullManualHref}
            target="_blank"
            rel="noopener noreferrer"
            className="venue-conductor-ui-icon-button is-ghost"
            aria-label="Open the full Help Center in a new tab"
            title="Open the full Help Center in a new tab"
          />
        </div>
      </header>
      <div className="venue-conductor-manual-panel-tabs" role="tablist" aria-label="Help Center sections">
        <button
          type="button"
          role="tab"
          aria-selected={showingArticles}
          className={`venue-conductor-manual-panel-tab ${showingArticles ? 'active' : ''}`}
          onClick={() => setView('articles')}
        >
          Articles
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!showingArticles}
          className={`venue-conductor-manual-panel-tab ${showingArticles ? '' : 'active'}`}
          onClick={() => setView('support_requests')}
        >
          Support requests
        </button>
      </div>
      {showingArticles && sections && (
        <div className="venue-conductor-manual-panel-search">
          <UserManualSearch sections={sections} onSelectArticle={showArticle} />
        </div>
      )}
      <div ref={bodyRef} className="venue-conductor-manual-panel-body">
        {!showingArticles && <ConductorSupportRequestsList onAskConductor={onAskConductor} />}
        {showingArticles && loading && <div className="venue-conductor-manual-panel-status">Loading the Help Center…</div>}
        {showingArticles && error && (
          <div className="venue-conductor-manual-panel-status">
            <p>Couldn&apos;t load the Help Center.</p>
            <Button variant="secondary" size="sm" className="venue-conductor-manual-panel-retry" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}
        {showingArticles && sections && activeEntry && (
          <>
            <Button variant="link-gray" size="sm" icon={ChevronLeft} className="venue-conductor-ui-browse venue-conductor-manual-panel-back" onClick={() => setActiveKey(null)}>
              All articles
            </Button>
            <UserManualArticle
              section={activeEntry.section}
              chapter={activeEntry.chapter}
              article={activeEntry.article}
              prevArticle={prevArticle}
              nextArticle={nextArticle}
              onNavigate={showArticle}
            />
          </>
        )}
        {showingArticles && sections && !activeEntry && (
          <UserManualSidebar sections={sections} activeKey={lastViewedKey} onSelectArticle={showArticle} />
        )}
      </div>
    </aside>
  );
};

ConductorManualPanel.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    chapters: PropTypes.array.isRequired
  })),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onAskConductor: PropTypes.func.isRequired,
  requestedArticle: PropTypes.shape({
    key: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  })
};

export default ConductorManualPanel;
